import { GameState } from './GameState';
import { GameConfig } from '../core/GameConfig';
import { EventBus, GameEvents } from '../core/EventBus';
import { GameData } from '../data/GameData';
import { rankSystem } from './RankSystem';
import { scriptAirMonths, scriptPremiereAbsOf, scriptShootWeeks, shootStartAbsFor } from './ScriptSystem';
import { fanSystem } from './FanSystem';
import { NormalNpc, NpcJob, ScriptDef, ScriptRoleDef, ScriptGenre, ScriptTheme } from '../data/types';
import { generateNormalNpcs } from '../data/tables/NormalNpcData';

/**
 * 普通 NPC 系统（单例）
 * 开局生成一批普通 NPC，跨季度常驻；
 * 玩家不签的剧本角色随机派给普通 NPC（性别 + 咖位匹配，无则顺延次一级咖位）
 *
 * 定稿规则：
 *  - 普通 NPC 只有名气/咖位（无六维属性），不真正走试镜流程；
 *  - 接角条件：性别符合 && 咖位 ≥ 角色等级；无符合者顺延给次一级咖位（递减找最高可用）；
 *  - 批6 生命周期：接角只登记在途工作（拍摄期→播出期），名气不再即时到账，
 *    改为月末随播出滴灌（与玩家合同同款公式与节奏）；播完结算 filmography（评分=剧本基础分）；
 *  - 批6 过气衰减：剧本池刷新时判定，整季无拍摄/播出工作 → 名气按比例 −= max(1, round(fame×NPC_FAME_DECAY_RATE))，
 *    允许掉咖位（玩家仍只升不降）；衰减率数值为占位，全部批次完成后由用户统一定稿；
 *  - 接角不结识（favor 保持 -1），结识走通告拍摄周偶遇（见 EncounterSystem.roll，初遇 VN 选友好才入页）；
 *  - 派角时机 = **季初换新时**（⛔ 挪到玩家选完之后：那样 NPC 整季晚开工 = 全行业档期后移一季、评选窗口归属全变），
 *    因此玩家抢到位时要 `revokeRole` 摘掉 NPC 那份、玩家弃演时要 `reclaimRole` 补派，
 *    同一个 (scriptId, roleIndex) 全周目只允许一个饰演者（否则奖项候选池会互相顶掉）。
 */
export class NormalNpcSystem {
    private static instance: NormalNpcSystem;

    private constructor() { }

    static getInstance(): NormalNpcSystem {
        if (!this.instance) {
            this.instance = new NormalNpcSystem();
        }
        return this.instance;
    }

    /** 按 ID 查普通 NPC（未找到返回 undefined） */
    findNpc(id: string): NormalNpc | undefined {
        return this.getNpcs().find(n => n.id === id);
    }

    /** 获取玩家名下普通 NPC 列表 */
    getNpcs(): NormalNpc[] {
        return GameState.getInstance().player.normalNpcs;
    }

    /** 开局生成一批普通 NPC（lastWorkAbsMonth = 开局月：新 NPC 首季豁免过气衰减） */
    generateBatch(count: number): void {
        const state = GameState.getInstance();
        const abs = state.player.year * 12 + state.player.month;
        state.player.normalNpcs = generateNormalNpcs(count);
        for (const npc of state.player.normalNpcs) {
            npc.activeJobs = [];
            npc.filmography = [];
            npc.lastWorkAbsMonth = abs;
        }
    }

    /**
     * 实例化竞争型特殊 NPC 注入派角池（2026-10-15）：把有 `rival` 的特殊 NPC 变成派角池里的竞争者，
     * 从此接戏/拍摄/杀青/播出/履历/名气滴灌/过气衰减全部白嫖普通 NPC 逻辑。
     * 身份字段（id/name/role/gender/avatar）**从 NPCDef 派生**——同一 id 的「社交视图（NPCDef）+ 生涯视图（本实例）」，
     * ⛔ 不手写第二份副本；改 NPCDef 的名字/立绘时实例化自然同步。
     * 幂等：已在池则跳过（开局建池 + 读档补迁移两处共用同一入口）。
     */
    generateSpecialCompetitors(): void {
        const state = GameState.getInstance();
        const abs = state.player.year * 12 + state.player.month;
        for (const sp of GameData.SPECIAL_NPCS) {
            if (!sp.rival) continue;                                            // 只有竞争型演员进派角池
            if (state.player.normalNpcs.some(n => n.id === sp.npcId)) continue; // 幂等（老档已含则跳过）
            const def = GameData.NPC_MAP[sp.npcId];
            if (!def) continue;                                                 // 无主表条目＝数据错，交读表桩
            // 初值自洽校验（2026-10-23 H 案批·QA 令）：rival 的 fame 必须 ≥ 其 rank 的门槛，否则首次滴灌/
            // 衰减一重算就自己掉档——10-22 门槛表改等比后，表里残留的旧表值 650（写「＝Lv.5 门槛」）正是这个病，
            // 玩家看到的「罗一鸣卡在小有名气」由此而来。这是「全库同步点清单」第 4 条（rival 初值）的守卫：
            // 下次改门槛表，开局就会喊，不用等实测抓。⛔ 上玩家屏（只 console）。
            const expectFloor = rankSystem.getRankThreshold(sp.rival.rank);
            if (sp.rival.fame < expectFloor) {
                console.warn(`[NormalNpc] 竞争型 ${def.name} 的 rival.fame=${sp.rival.fame} 低于 rank=${sp.rival.rank} 的门槛 ${expectFloor} ⇒ 首次结算会掉咖位；改门槛表后须同步 rival 初值（见导入台账「全库同步点清单」）`);
            }
            state.player.normalNpcs.push({
                id: def.id,
                name: def.name,
                role: def.role,
                gender: def.gender,
                icon: def.icon,
                avatar: def.avatar,
                personality: 'special',        // 文案走 NPCDef 专属池（SocialSystem/EncounterSystem 先查 NPC_MAP）
                fame: sp.rival.fame,
                rank: sp.rival.rank,
                activeJobs: [],
                filmography: [],
                lastWorkAbsMonth: abs,
            });
        }
    }

    /**
     * 为角色指派普通 NPC（玩家未签的角色）—— 2026-09-28 批8 等位优先（甲′案）
     * 1. ① 首选**咖位=角色等级**的 NPC 随机抽 1
     * 2. ② 无同级可用 → 逐级**向下**顺延（role.level-1 递减取最高可用咖位，低咖演高角=晋升通道，批D 口径保留）
     * 3. ③ 向下也全无 → 逐级**向上**兜底（role.level+1 递增找最低可用咖位）——用户红线「⛔ 无人接作废」，
     *    兜底保证除性别极端撞车外角色必有归宿（40 run 模拟：兜底仅 0.18 次/周目，修正力度与纯作废案全等）
     * 4. ④ 全无（如男角×全女池）→ null，本季度作废
     * 改前口径=「rank ≥ level 随机抽」：Lv9 也进 Lv2 候选池 ⇒ 高咖永不空季、过气衰减不可达（18 年模拟仅掉咖 9 次
     * vs 本案 17 次，顶端 Lv.9→Lv.8），即用户实测「高等级 NPC 只涨不掉」的病根。
     * 2026-10-01 追加（用户实测「同季两部剧男女主是同样两个 NPC」拍板甲案）：主演位档期互斥。
     * 2026-10-02 扩散（用户拍板）：互斥范围从「男主角/女主角」（roleType 字面）改为 **番位 billing ≤ 2**
     * （一番+二番位）——《醒木》（群像剧无男女主）的「男二号」付一刀 billing=2 实为全剧一番，
     * roleType 判定防不住「一番配角」变体；番位才是角色地位的真相源。billing≤2 的位接取前提=
     * 手头无任何在拍工作（同夜收口互斥，丙′ 下对往季零影响）；三番开外与无番位（综艺）自由轧戏。
     * ④兜底（先完全空闲、全占才让在拍者轧、⛔ null 作废）随互斥范围扩展覆盖全部 billing≤2 位。
     */
    /**
     * 检查某竞争型 NPC 是否符合接戏限制（2026-10-20 新增）：
     * 只约束随机派发，不影响角色锚点（roleAnchors）的固定安排。
     */
    private checkCompetitorPreferences(npcId: string, script: ScriptDef, role: ScriptRoleDef): boolean {
        const sp = GameData.SPECIAL_NPCS.find(s => s.npcId === npcId);
        if (!sp?.rival?.auditionLimits) return true; // 非竞争型或无限制 ⇒ 全部放行

        const limits = sp.rival.auditionLimits;
        const { preferredGenres, excludedGenres, preferredThemes, excludedThemes, maxBilling, blockedYears } = limits;

        // 类型限制：只接的类型
        if (preferredGenres && preferredGenres.length > 0) {
            if (!preferredGenres.includes(script.genre)) return false;
        }

        // 类型限制：不接的类型
        if (excludedGenres && excludedGenres.length > 0) {
            if (excludedGenres.includes(script.genre)) return false;
        }

        // 阶段类型排除：按年段生效（2026-11-03，黎青「第 8 年起不接短剧」）
        const phaseExcluded = this.phaseExcludedGenresOf(npcId);
        if (phaseExcluded.length > 0 && phaseExcluded.includes(script.genre)) return false;

        // 题材限制：只接的题材（命中任意一个即可）
        if (preferredThemes && preferredThemes.length > 0) {
            const hasPreferred = script.themes.some(t => preferredThemes.includes(t));
            if (!hasPreferred) return false;
        }

        // 题材限制：不接的题材
        if (excludedThemes && excludedThemes.length > 0) {
            const hasExcluded = script.themes.some(t => excludedThemes.includes(t));
            if (hasExcluded) return false;
        }

        // 阶段题材排除：按年段生效（2026-11-03，黎青「第 8 年起不接偶像/仙侠/恐怖」）
        const phaseExcludedThemes = this.phaseExcludedThemesOf(npcId);
        if (phaseExcludedThemes.length > 0 && script.themes.some(t => phaseExcludedThemes.includes(t))) return false;

        // 番位限制：最高番位
        if (maxBilling !== undefined && maxBilling > 0) {
            const billing = role.billing ?? 99;
            if (billing > maxBilling) return false;
        }

        // 时间限制：不接戏的年份区间
        if (blockedYears && blockedYears.length > 0) {
            const state = GameState.getInstance();
            // player.year 是 0 起（第1年 = 0），blockedYears 按展示年写（1 起）⇒ 换到展示口径再比
            const currentYear = state.player.year + 1;
            for (const range of blockedYears) {
                if (currentYear >= range.from && currentYear <= range.to) return false;
            }
        }

        // 阶段接戏门槛（2026-10-24 软限制；数值定值=导入台账 §7.3 ④‴）：当年她能接的角色等级上限。
        // 这是竞争型「爬得急 / 天花板低」的实现载体——咖位由「能接到什么档的戏 → 名气涨多少」自然推出，
        // ⛔ 不写死年份咖位（甲案锚点曲线已作废）。只拦**随机派发**，roleAnchors 剧情锚点照旧。
        const levelCap = this.phaseRoleLevelCapOf(npcId);
        if (levelCap !== null && role.level > levelCap) return false;

        return true;
    }

    assignNormalNpc(role: ScriptRoleDef, scriptId: string): NormalNpc | null {
        const npcs = this.getNpcs();
        const script = GameData.SCRIPT_MAP[scriptId];
        const genderOk = (n: NormalNpc) => role.gender === 'any' || n.gender === role.gender;
        const notSameScript = (n: NormalNpc) => !(n.activeJobs ?? []).some(r => r.scriptId === scriptId);
        // 前两番位（billing ≤ 2，含群像剧的「一番配角」）档期互斥：在拍⛔再接
        const isFront = (role.billing ?? 99) <= 2;
        const frontFree = (n: NormalNpc) => !isFront || !(n.activeJobs ?? []).some(j => j.phase === 'shooting');
        // 接戏限制过滤（2026-10-20 新增）：竞争型 NPC 的偏好只约束随机派发
        const prefsOk = (n: NormalNpc) => this.checkCompetitorPreferences(n.id, script, role);
        const pickFrom = (pool: NormalNpc[]): NormalNpc | null =>
            pool.length > 0 ? pool[Math.floor(Math.random() * pool.length)] : null;

        // ① 等位优先：咖位=角色等级
        let hit = pickFrom(npcs.filter(n => n.rank === role.level && genderOk(n) && notSameScript(n) && frontFree(n) && prefsOk(n)));
        if (hit) return hit;

        // ② 向下顺延：找最高的低于等级的可用咖位（低咖上爬通道）
        for (let r = role.level - 1; r >= 1; r--) {
            hit = pickFrom(npcs.filter(n => n.rank === r && genderOk(n) && notSameScript(n) && frontFree(n) && prefsOk(n)));
            if (hit) return hit;
        }

        // ③ 向上兜底：找最低的高于等级的可用咖位（防作废；高咖下凡只剩这一道缝）
        for (let r = role.level + 1; r <= GameConfig.RANK_MAX; r++) {
            hit = pickFrom(npcs.filter(n => n.rank === r && genderOk(n) && notSameScript(n) && frontFree(n) && prefsOk(n)));
            if (hit) return hit;
        }

        // ④ 前两番互斥全空兜底（批8 红线「⛔ 无人接作废」）：分两层——优先**完全空闲**（无任何在途 job）
        // 的 NPC 接天窗，全占才让在拍者轧戏（⛔ 未分层时会在「空闲者」与「在拍者」间随机，
        // 可能给已拍另一部女主戏的 NPC 再塞一部女主=同 NPC 双主演轧戏）；
        // 拍摄期重叠属可接受 fiction，比空位作废好
        if (isFront) {
            hit = pickFrom(npcs.filter(n => genderOk(n) && notSameScript(n) && prefsOk(n) && (n.activeJobs ?? []).length === 0));
            if (hit) return hit;
            hit = pickFrom(npcs.filter(n => genderOk(n) && notSameScript(n) && prefsOk(n)));
            if (hit) return hit;
        }

        return null;
    }

    /**
     * 「这一位全周目演过没有」＝派角唯一的去重尺（2026-10-18 治本，茄子抓虫：玩家参演过的《颠倒姻缘》
     * 播完几个月后，热度榜又出现一版主演全是 NPC 的同一个剧）。
     *
     * 换掉的旧尺是「当前在途」（`fillRole` / `reclaimRole` 只扫 `activeJobs`）：NPC 一版拍完，那份 job
     * 就在同一次月末结算里搬进 `filmography`、从在途里摘掉，于是下一夜再数在途时这一位又成空的，
     * 同一本剧每逢换新夜被整组 NPC 重拍一遍（无头实测 14 个月：NPC 履历 12 条、名气按 heat 发了四遍、
     * 每一遍都进 `AwardSystem.allWorks` 的参评名单）。类头第 26 行早就写明口径是
     * 「同一个 (scriptId, roleIndex) 全周目只允许一个饰演者」，这次是把尺对齐口径，不是新规矩。
     *
     * 账本取三处永久记录，缺一就会漏判：
     *  - `player.contracts`：签约到作废为止都留着（done/airing 永久保留，见 resetForMarketRefresh）；
     *  - `player.filmography`：玩家播完落账；
     *  - 每个 NPC 的 `activeJobs`（在途）与 `filmography`（拍完落账）。
     * 玩家弃演补派（`reclaimRole`）不受影响：合同在补派前已从列表移除、作废位也不写履历，
     * 所以那一位在账上确实是空的，照旧派得出去。
     */
    roleEverPlayed(scriptId: string, roleIndex: number): boolean {
        const state = GameState.getInstance();
        if (state.player.contracts.some(c => c.scriptId === scriptId && c.roleIndex === roleIndex)) return true;
        if ((state.player.filmography ?? []).some(f => f.scriptId === scriptId && f.roleIndex === roleIndex)) return true;
        for (const npc of this.getNpcs()) {
            if ((npc.activeJobs ?? []).some(j => j.scriptId === scriptId && j.roleIndex === roleIndex)) return true;
            if ((npc.filmography ?? []).some(f => f.scriptId === scriptId && f.roleIndex === roleIndex)) return true;
        }
        return false;
    }

    /** 本剧是否还有没人演过的位（`roleEverPlayed` 的整剧视图，不许另立判据；给收口 sweep 当终止条件用） */
    hasUnplayedRole(scriptId: string): boolean {
        const script = GameData.SCRIPT_MAP[scriptId];
        if (!script) return false;
        return script.roles.some((_, i) => !this.roleEverPlayed(scriptId, i));
    }

    /**
     * 该剧是否有 NPC 挂着在途工作（进组拍摄中 / 播出滴灌中）。
     * 试镜池「剧级撤下」判据之一（2026-10-25 茄子令：玩家签约后该剧即刻完成选角——其余角色
     * `bindContractCast` 现场派 NPC 进组）：NPC 班底已开拍的剧 ⛔ 再出现在试镜剧本册。
     * 只看在途不看履历（`roleEverPlayed`）——「演过」管的是派角去重，这边管的是「选角季已结束」。
     */
    hasActiveJob(scriptId: string): boolean {
        return this.getNpcs().some(n => (n.activeJobs ?? []).some(j => j.scriptId === scriptId));
    }

    /**
     * 为剧本的一个「未占用」角色位派角（2026-10-15 抽自 sweepUnclaimedRoles / bindContractCast 的同款循环，消除双写）：
     *  1. 这一位全周目已经有人演过（在途或已入履历）就跳过（一部剧一个位只拍一遍，尺见 roleEverPlayed）；
     *  2. 该位有角色锚点则直接派给锚点特殊 NPC（exclusive 必演 / fallback 兜底）；
     *  3. 否则随机派普通 NPC（assignNormalNpc 的等位/顺延/兜底规则）。
     * 调用方负责「玩家的位要跳过」这类前置判据（玩家占位口径各不相同，不塞进来）。
     * @param absAt 派角那一晚的当月（缺省＝当前月）：收口夜乙段在换月之后才派，
     *              开机月仍按收口夜那一晚推（不然整组 NPC 的开工月顺延一个月）
     */
    fillRole(script: ScriptDef, roleIndex: number, absAt?: number): void {
        const role = script.roles[roleIndex];
        if (!role) return;
        if (this.roleEverPlayed(script.id, roleIndex)) return;
        const anchor = GameData.roleAnchorOf(script.id, role.name);
        if (anchor) {
            const npc = this.findNpc(anchor.npcId);
            if (npc) { this.grantRoleToNpc(npc, script, role, absAt); return; }
            // 锚点 NPC 不在派角池（未实例化 / 数据错）⇒ 喊一句再回落随机，别让角色位悬空
            console.warn(`[NormalNpc] 《${script.name}》角色「${role.name}」锚定 ${anchor.npcId}，但该 NPC 不在派角池 ⇒ 回落随机派角`);
        }
        const npc = this.assignNormalNpc(role, script.id);
        if (npc) this.grantRoleToNpc(npc, script, role, absAt);
    }

    /**
     * 授予普通 NPC 一个角色（批6）：只登记在途工作，不加名气——
     * 名气 = heat × 番位系数 接角时快照进 job.fameTotal，播出期月末滴灌；
     * 档期走剧级统一口径（2026-09-28 统一档期批）：拍摄周数 = scriptShootWeeks（全角色同值，同剧同杀青同开播），
     * 播出期长度走 scriptAirMonths（剧覆盖位 ?? 类型固定表，与玩家 airMonthsOf 同口径）。
     * 同剧本不重复接（assignNormalNpc 已过滤）。
     * @param absAt 派角那一晚的当月（缺省＝当前月）：收口夜乙段在换月之后才派，
     *              开机月与过气起点仍按收口夜那一晚算
     */
    grantRoleToNpc(npc: NormalNpc, script: ScriptDef, role: ScriptRoleDef, absAt?: number): void {
        const state = GameState.getInstance();
        const abs = absAt ?? state.player.year * 12 + state.player.month;
        const factor = role.billing === undefined
            ? GameConfig.FAME_FACTOR_NO_BILLING
            : GameConfig.FAME_BILLING_FACTORS[Math.min(role.billing, 5)];
        const airMonths = scriptAirMonths(script);
        const shootWeeks = scriptShootWeeks(script);
        const job: NpcJob = {
            scriptId: script.id,
            roleIndex: script.roles.indexOf(role),
            phase: 'shooting',
            shootWeeksLeft: shootWeeks,
            // 开拍月（2026-10-27 滚动排期）：开机月前不递减——全剧组同月开机同月杀青
            shootStartAbs: shootStartAbsFor(script.id, abs),   // 新手剧当月直拍（与合同侧同走 shootStartAbsFor）
            airMonths,
            // 剩余发放月数 = 播出期 + 1（杀青当月月末滴首月，批6 修订）
            airMonthsLeft: airMonths,   // 首滴=开播月（档期次月），发放月数=播出期（2026-10-27 新口径）
            // NPC 侧滴灌保留旧式「heat × 番位系数」**基数**，再乘名气收入倍率：
            //  - 普通 NPC（群演）倍率恒为 1 —— 他们的咖位玩家看不见，抬全池水位会挤占玩家的当季角色位
            //    （Lv.6 角色更多 NPC 来抢）；旧注释担心的「头部 NPC 十八年爬到 Lv.9~10 抢角色」这条
            //    对群演仍然有效，⛔ 不要给他们加倍率。
            //  - 竞争型特殊 NPC 乘 `fameIncomeMult ?? NPC_FAME_INCOME_MULT`（2026-10-24 数值定值 ×30）：
            //    他们要「爬上去」才有软限制可言——旧式一部戏 +35~50，而门槛表已抬成等比 ×3.7，
            //    不乘倍率数学上爬不动（Lv.5→6 差 2900，要拍 ~70 部）。
            //  - ⛔ 仍不与玩家侧 `ScriptSystem.fameGainOf` 并表：那套带咖位乘子与名气因子，NPC 用不上。
            fameTotal: Math.round(script.heat * factor * this.rivalFameIncomeMultOf(npc.id)),
            famePaid: 0,
        };
        npc.activeJobs = (npc.activeJobs ?? []).concat(job);
        npc.lastWorkAbsMonth = abs;
        const roleLabel = role.name ? `${role.roleType}「${role.name}」` : role.roleType;
        // 不再直接 meetNpc —— 结识改由通告拍摄周偶遇触发（同剧组同事，初遇 VN 选友好才入页）
    }

    /**
     * 回收一个已被 NPC 持有的角色位（玩家签约/带资抢到同一个位时调用）
     *
     * 为什么要这道工序（2026-09-29 玩家「永无提名」缺陷的根因）：
     *  - 季末换新时 `GameState.sweepUnclaimedRoles()` 会**先**把整季所有角色位派给 NPC
     *    （此时玩家还没开始试镜，⛔ 能判定谁放弃），玩家是在同一季内才抢到位；
     *  - 若玩家签下来而 NPC 那份⛔ 撤销 ⇒ 同一个 (scriptId, roleIndex) 两边各拍各的、
     *    各写一条履历；NPC 早接角一个月 ⇒ 播完也早一个月，AwardSystem.allWorks()
     *    「同位只留最早播完一次」的去重会把**玩家那条静默丢掉** ⇒ 玩家既进不了提名池也拿不到奖。
     *  - 所以玩家一旦定角，就把还在在途（拍摄/播出）的 NPC 那份摘掉；已入履历的摘不掉，
     *    由 AwardSystem 去重侧的「玩家行优先」兜底（见 AwardSystem.allWorks）。
     *
     * @returns 被摘掉的 NPC（旧档同位重复派出过时返回第一个，全部摘净）；无人持有 ⇒ null
     */
    revokeRole(scriptId: string, roleIndex: number): NormalNpc | null {
        let first: NormalNpc | null = null;
        for (const npc of this.getNpcs()) {
            const jobs = npc.activeJobs ?? [];
            const left = jobs.filter(j => !(j.scriptId === scriptId && j.roleIndex === roleIndex));
            if (left.length === jobs.length) continue;
            npc.activeJobs = left;
            first ??= npc;      // 旧档可能重复派出过（同位两人）⇒ 全清，日志只报第一个
        }
        return first;
    }

    /**
     * 补派一个玩家弃演的角色位（主动解约 / 通告超期作废时调用）
     * 与 revokeRole 成对：玩家把位让出来了，重新派给 NPC，⛔ 留下「无人饰演」的空位
     * （候选池容量由排期表唯一决定，空位会让该届该类别少一个候选）。
     * 2026-10-15：改为走 `fillRole` 统一路径——补派时也认锚点（fallback 玩家弃演 ⇒ 角色回到锚点 NPC，
     * ⛔ 随机派给别人）。返回接了角的 NPC；无人可派（性别/咖位全被占）时 null。
     */
    reclaimRole(scriptId: string, roleIndex: number): NormalNpc | null {
        const script = GameData.SCRIPT_MAP[scriptId];
        if (!script) return null;
        // 去重交给 `fillRole` 里的同一把尺（2026-10-18：这里原样抄过一遍「在途」判定，与 `roleEverPlayed`
        // 同一条口径两处维护，正是重复派发的温床），锚点匹配规则也不在这里复述
        this.fillRole(script, roleIndex);
        return this.getNpcs().find(n => (n.activeJobs ?? []).some(j => j.scriptId === scriptId && j.roleIndex === roleIndex)) ?? null;
    }

    /**
     * 月末在途工作推进（批6，玩家 advanceAiringContracts 的同款节拍）：
     * 拍摄期每月 −4 周，≤0 → 杀青当月月末即滴灌首月（播出期本身从次月起算，发放月数=播出期+1）；
     * 每月 −1、名气按 round(fameTotal÷airMonths) 滴灌（末月=播出期末月，发全部余数，Σ=fameTotal）；首滴=开播月（档期次月）
     * 播完 → filmography 入档（NPC 评分 = 剧本基础分，定稿：不掷属性加成、无数值效果）、
     * lastWorkAbsMonth 刷新（过气宽限起点）、移出在途。
     */
    advanceMonthJobs(): void {
        const state = GameState.getInstance();
        const abs = state.player.year * 12 + state.player.month;
        for (const npc of this.getNpcs()) {
            const jobs = npc.activeJobs ?? [];
            if (jobs.length === 0) continue;
            const keep: NpcJob[] = [];
            for (const job of jobs) {
                const script = GameData.SCRIPT_MAP[job.scriptId];
                const role = script?.roles[job.roleIndex];
                if (job.phase === 'shooting') {
                    // 开机闸（2026-10-27 滚动排期）：收口夜/签约夜建的 job 在开机月前不递减——
                    // 全剧组同月开机同月杀青。「剧组等主角」挂起随统一开拍钟废除（旧档 job 无
                    // shootStartAbs 走旧钟，永不挂起也无需挂起）。
                    if (job.shootStartAbs != null && abs < job.shootStartAbs) { keep.push(job); continue; }
                    job.shootWeeksLeft -= GameConfig.WEEKS_PER_MONTH;
                    if (job.shootWeeksLeft > 0) { keep.push(job); continue; }
                    // NPC 杀青：翻 airing（杀青≠开播，滴灌自开播月起——下方开播闸统一控制）
                    job.phase = 'airing';
                }
                // —— 开播闸（2026-10-27 茄子令「档期结束下一个月开播」＝2026-10-29 定调的剧杀青次月开播）：
                //     与玩家侧同口径（scriptPremiereAbsOf），全剧统一上线
                if (script && job.shootStartAbs != null && abs < scriptPremiereAbsOf(job.shootStartAbs, script)) {
                    keep.push(job); continue;
                }
                // —— 播出期滴灌（发放月数 = 播出期）——
                const paid = job.famePaid;
                const payoutMonths = Math.max(1, job.airMonths);
                job.airMonthsLeft = Math.max(0, job.airMonthsLeft - 1);
                const drip = job.airMonthsLeft <= 0
                    ? Math.max(0, job.fameTotal - paid)
                    : Math.min(job.fameTotal - paid, Math.round(job.fameTotal / payoutMonths));
                if (drip > 0) {
                    // 2026-10-24 用户改令：甲案锚点曲线整套作废 ⇒ 竞争型**恢复普通路径**（滴灌 + 过气衰减，
                    // 单时钟）。她的咖位改由「阶段接戏门槛（`phaseRoleLevelCaps`）→ 能接到什么档的戏 →
                    // 滴灌名气 → rank 重算」自然推出，每局会浮动（软限制的含义）。
                    const oldRank = npc.rank;
                    npc.fame += drip;
                    job.famePaid += drip;
                    npc.rank = rankSystem.getRankFromFame(npc.fame);
                    // 晋升播报只给竞争型（普通 NPC 15 人全池会刷屏）；降级由过气衰减那条统一播
                    if (this.isRival(npc)) this.announceRivalRankChange(npc, oldRank);
                }
                if (job.airMonthsLeft > 0) { keep.push(job); continue; }
                // —— 播完：入履历 + 刷新最后工作月 ——
                const score = script?.baseScore ?? 0;
                (npc.filmography ??= []).push({
                    scriptId: job.scriptId,
                    roleIndex: job.roleIndex,
                    // 与玩家侧同款快照（见 FilmographyEntry 注释）：⛔ 让展示字段随剧本表回改而漂移
                    workName: script?.name ?? '已下架作品',
                    roleType: role?.roleType ?? '',
                    billing: role?.billing,
                    genre: script?.genre ?? 'other',
                    score,
                    // 「爆」标定格（热度榜同口径 workHeat ≥ HEAT_BOOM_LINE，与玩家履历一致）
                    boom: (script ? fanSystem.workHeatOf(script) : 0) >= GameConfig.HEAT_BOOM_LINE,
                    year: state.player.year,
                    month: state.player.month,
                });
                npc.lastWorkAbsMonth = abs;
                const roleLabel = role
                    ? (role.name ? `${role.roleType}「${role.name}」` : role.roleType)
                    : '';
            }
            npc.activeJobs = keep;
        }
    }

    /**
     * 是否**特殊人脉**（`NPC_MAP` / `SPECIAL_NPCS` 里手写的特殊 NPC，含竞争型与资源型）：
     * 人脉页「删除好友」的唯一排除判据（2026-11-01 用户令「所有特殊 NPC 都删掉删除键」）。
     * **全库唯一读取口**，界面侧 ⛔ 自己遍历 `SPECIAL_NPCS`（第二把尺）。
     */
    isSpecialNpc(npcId: string): boolean {
        return GameData.SPECIAL_NPCS.some(s => s.npcId === npcId);
    }

    /**
     * 是否竞争型特殊 NPC（**全库唯一读取口**）：读现成的身份标记（`SPECIAL_NPCS[].rival`），
     * ⛔ 另立第二个「她是 rival」的字段＝第二把尺。界面侧也走这个口（⛔ 不自己遍历 `SPECIAL_NPCS`）。
     * 用途（2026-10-24 用户改令后收窄；2026-11-01 删按钮判据改由 `isSpecialNpc` 承担）：
     *  ① 咖位跨档的业内播报：竞争型晋升/降级各播一条，普通 NPC（群演）只播降级、晋升不播
     *     （全池 15 人加晋升播报会刷屏，KNOWN 代价）。
     * 历史注：甲案时期它还是「咖位曲线单时钟」的双豁免判据（跳过滴灌与过气衰减）；
     * 2026-10-24 曲线整套作废、rival 回到与普通 NPC 同一条时钟 ⇒ 那两处豁免已撤。
     */
    isRival(npc: NormalNpc): boolean {
        return GameData.SPECIAL_NPCS.some(s => s.npcId === npc.id && !!s.rival);
    }

    /**
     * 竞争型咖位跨档的业内播报（2026-10-24 恢复滴灌后补：「她能接到什么戏 → 名气涨多少 → 咖位浮动」
     * 成了玩家观察她成长的唯一窗口，播报是那个缺信号的解法）。
     * 口径（README §三）：只播「谁 + 咖位名变了」，⛔ 归因到作品（滴灌是多剧混合、因果不可指认）、
     * ⛔ 出现 `Lv.`；普通 NPC 晋升一律不播（走这里的前置判据 `isRival`）。
     * 调用点＝滴灌处 rank 变化后（一个月滴灌一次 ⇒ 同月只播最终那一档，天然成立）。
     */
    private announceRivalRankChange(npc: NormalNpc, oldRank: number): void {
        if (npc.rank === oldRank) return;
        const rankName = rankSystem.getRankName(npc.rank, npc.gender);
    }

    /**
     * **名气收入倍率取数**（2026-10-24；数值定值=导入台账 §7.3 ④″）：
     * 竞争型取 `rival.fameIncomeMult ?? GameConfig.NPC_FAME_INCOME_MULT`；其余（普通群演）恒为 1。
     * ⛔ 只有这一处读取口——别处再判一次「她是不是竞争型」即第二把尺（判据走 `isRival`／本方法）。
     */
    private rivalFameIncomeMultOf(npcId: string): number {
        const rival = GameData.SPECIAL_NPCS.find(s => s.npcId === npcId)?.rival;
        if (!rival) return 1;
        return rival.fameIncomeMult ?? GameConfig.NPC_FAME_INCOME_MULT;
    }

    /**
     * **阶段接戏门槛取数**（2026-10-24 软限制；数值定值=导入台账 §7.3 ④‴，规格=README §三）：
     * 返回当年她能接的**角色等级上限**；未配 `phaseRoleLevelCaps` ⇒ null（＝不限）。
     * `fromYear` ＝展示年（第 1 年 = 1，与 `blockedYears` 同口径），取**最后一个 `fromYear ≤ 当年`** 的档
     * （⛔ 不写「升级后回落」的档，那会让咖位来回跳——甲案插值震荡的教训）。
     * 只约束**随机派发**，`roleAnchors` 剧情锚点照旧 fallback（README §三 明文）。
     */
    private phaseRoleLevelCapOf(npcId: string): number | null {
        const caps = GameData.SPECIAL_NPCS.find(s => s.npcId === npcId)?.rival?.auditionLimits?.phaseRoleLevelCaps;
        if (!caps || caps.length === 0) return null;
        const curYear = GameState.getInstance().player.year + 1;   // 展示年口径
        let hit: number | null = null;
        for (const c of caps) { if (c.fromYear <= curYear) hit = c.maxRoleLevel; }
        return hit;
    }

    /**
     * **阶段类型排除取数**（2026-11-03；规格=README §三）：返回当年生效的排除体裁；
     * 未配或无满足档 ⇒ 空数组（＝不限）。取最后一个 `fromYear ≤ 当年` 的档，与 `phaseRoleLevelCaps` 同口径。
     */
    private phaseExcludedGenresOf(npcId: string): ScriptGenre[] {
        const phases = GameData.SPECIAL_NPCS.find(s => s.npcId === npcId)?.rival?.auditionLimits?.phaseExcludedGenres;
        if (!phases || phases.length === 0) return [];
        const curYear = GameState.getInstance().player.year + 1;
        let hit: ScriptGenre[] = [];
        for (const p of phases) { if (p.fromYear <= curYear) hit = p.genres; }
        return hit;
    }

    /** **阶段题材排除取数**（2026-11-03）：返回当年生效的排除题材；口径同 `phaseExcludedGenresOf`。 */
    private phaseExcludedThemesOf(npcId: string): ScriptTheme[] {
        const phases = GameData.SPECIAL_NPCS.find(s => s.npcId === npcId)?.rival?.auditionLimits?.phaseExcludedThemes;
        if (!phases || phases.length === 0) return [];
        const curYear = GameState.getInstance().player.year + 1;
        let hit: ScriptTheme[] = [];
        for (const p of phases) { if (p.fromYear <= curYear) hit = p.themes; }
        return hit;
    }

    /**
     * → 名气按比例衰减 max(1, round(fame × NPC_FAME_DECAY_RATE))，下限 0，咖位随名气重算（允许降级）。
     * 在途（含播出期）不衰；lastWorkAbsMonth 在接角/播完时刷新，构成播完后的整季宽限。
     * ⚠️ 衰减率为占位值 0.10，最终由用户全批次定稿后统一拍板。返回衰减人数（供测试断言）。
     */
    applyObsolescenceDecay(): number {
        const state = GameState.getInstance();
        const abs = state.player.year * 12 + state.player.month;
        let decayed = 0;
        for (const npc of this.getNpcs()) {
            // 2026-10-24：甲案的「竞争型整条跳过」豁免已撤——曲线作废后 rival 与普通 NPC 同一条时钟，
            // 过气衰减照吃（这正是「她的咖位不该每年精确、会浮动」的来源之一）。
            if ((npc.activeJobs ?? []).length > 0) continue;
            const last = npc.lastWorkAbsMonth ?? abs;
            if (abs - last < GameConfig.SCRIPT_REFRESH_INTERVAL) continue;
            if (npc.fame <= 0) continue;
            const loss = Math.min(npc.fame, Math.max(1, Math.round(npc.fame * GameConfig.NPC_FAME_DECAY_RATE)));
            npc.fame -= loss;
            const oldRank = npc.rank;
            npc.rank = rankSystem.getRankFromFame(npc.fame);
            decayed++;
            const rankText = npc.rank < oldRank
                ? `，咖位跌到「${rankSystem.getRankName(npc.rank, npc.gender)}」`
                : '';
        }
        return decayed;
    }
}

/** 导出单例 */
export const normalNpcSystem = NormalNpcSystem.getInstance();
